import { classMerge } from "aic";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { useContext, useMemo } from "react";
import { Overlay, Popover } from "react-bootstrap";
import styles from "./AnnotationPreviewBubble.module.css";
import { AnnotationWhereGroup } from "./AnnotationWhereGroup";
import { generatePreviewText } from "./generatePreviewText";

/**
 * Props for the AnnotationPreviewBubble component.
 */
export interface AnnotationPreviewBubbleProps {
  /** Annotation object to preview. */
  annotation?: AnnotationWhereGroup;
  /** Additional CSS class names. */
  className?: string;
  /** Preferred bubble position for inline previews. */
  position?: "top" | "right" | "left";
  /** Display variant: 'column' (simple div) or 'bubble' (Bootstrap overlay) */
  variant?: "column" | "bubble";
  /** Click handler for interactive bubbles. */
  onClick?: () => void;
  /**
   * Ref to the target element that the annotation bubble should be anchored to.
   * Used for positioning the popover overlay or column preview.
   */
  target: React.RefObject<HTMLElement | null>;
  /**
   * Optional key to force Popper.js to recalculate position when structural changes occur.
   * Useful for Table annotations when columns are added/removed, as these changes don't
   * trigger scroll/resize events that Popper.js listens to.
   */
  repositionKey?: string | number;
}

/**
 * Unified component to display annotation preview.
 * - 'bubble' variant: Uses Bootstrap Overlay with Popover (for hover-triggered overlays)
 * - 'column' variant: Simple div with inline preview (for table cells)
 *
 * @param props - The component props
 * @param props.annotation - Annotation object to preview.
 * @param props.className - Additional CSS class names.
 * @param props.position - Preferred bubble position for inline previews.
 * @param props.variant - Visual variant; column previews render differently to bubble previews.
 * @param props.onClick - Click handler for interactive bubbles.
 * @param props.target - Ref to the target element that the annotation bubble should be anchored to.
 * @param props.repositionKey - Optional key to force Popper.js to recalculate position.
 * @returns JSX element displaying the annotation preview or null
 */
export const AnnotationPreviewBubble = ({
  annotation,
  className = "",
  position = "right",
  variant = "bubble",
  onClick,
  target,
  repositionKey,
}: AnnotationPreviewBubbleProps): React.ReactElement | null => {
  const appSettings = useContext(AppSettingsContext);

  /**
   * Container for Overlay popover. Uses target's parent or document.body.
   */
  /* istanbul ignore next */
  const container =
    target.current?.parentElement ?? (typeof document !== "undefined" ? document.body : undefined);

  /**
   * Memoized preview text for annotation clauses.
   */
  const previewText = useMemo(
    () =>
      annotation
        ? generatePreviewText(annotation.clauses, appSettings.annotationComparatorTypes)
        : "",
    [annotation, appSettings.annotationComparatorTypes],
  );

  /**
   * Keyboard event handler for accessibility.
   * Triggers onClick when Enter or Space is pressed.
   *
   * @param e - Keyboard event
   */
  const handleKeyDown = onClick
    ? (e: React.KeyboardEvent) => {
        /* istanbul ignore else */
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }
    : /* istanbul ignore next */ undefined;

  // Bubble variant - Bootstrap Overlay with Popover
  return !annotation || !annotation.clauses || annotation.clauses.length === 0 ? null : variant ===
    "column" ? (
    <div
      className={classMerge([styles.columnPreview, className])}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={handleKeyDown}
      aria-label={onClick ? "Annotation preview" : undefined}
    >
      <div className={styles.bubbleContent}>
        {previewText && <div className={styles.bubbleText}>{previewText}</div>}
      </div>
    </div>
  ) : (
    <Overlay
      key={repositionKey !== undefined ? `overlay-${repositionKey}` : undefined}
      placement={position}
      target={target?.current}
      show={true}
      container={container}
      popperConfig={{
        modifiers: [
          { name: "arrow", enabled: false },
          { name: "offset", options: { offset: [0, 2] } },
          { name: "eventListeners", options: { scroll: true, resize: true } },
          {
            name: "flip",
            enabled: true,
            options: {
              // Only allow horizontal flipping (left <-> right), not vertical (top <-> bottom)
              fallbackPlacements:
                position === "left" || position === "right"
                  ? [position === "left" ? "right" : "left"]
                  : [],
            },
          },
          { name: "preventOverflow", enabled: true, options: { boundary: "viewport" } },
        ],
      }}
    >
      <Popover
        id={`annotation-preview-${annotation.id}`}
        className={classMerge([styles.annotationPreviewBubble, className])}
        onClick={onClick}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={handleKeyDown}
        aria-label={onClick ? "Annotation preview bubble" : undefined}
      >
        <Popover.Body className={styles.bubbleContent}>
          {previewText && <div className={styles.bubbleText}>{previewText}</div>}
        </Popover.Body>
      </Popover>
    </Overlay>
  );
};

AnnotationPreviewBubble.displayName = "AnnotationPreviewBubble";
